import type { HttpInterceptorFn } from '@angular/common/http';

import { authInterceptor } from './auth.interceptor';
import { errorInterceptor } from './error.interceptor';
import { loadingInterceptor } from './loading.interceptor';

export { authInterceptor } from './auth.interceptor';
export { errorInterceptor } from './error.interceptor';
export { loadingInterceptor } from './loading.interceptor';

/**
 * THE ONLY PERMITTED BARREL IN THE APP.
 *
 * It exists because the ORDER below is load-bearing and belongs in one place
 * rather than being retyped, and possibly reordered, in app.config.ts:
 *
 *   loading  first - outermost, so one start/stop pair brackets the entire
 *                    request including the 401 refresh-and-retry. Inside auth,
 *                    the retry would read as a second request and the bar
 *                    would blink.
 *   auth     second - the retried request must carry the REFRESHED token, so
 *                    the header is applied inside the loading bracket.
 *   error    last   - innermost.
 *
 * NOTE ON THE RESPONSE PATH, because it is the opposite of the intuition and
 * an earlier version of FRONTEND_ARCHITECTURE.md got it wrong: the array is
 * outermost-to-innermost, so on the way BACK the innermost runs FIRST.
 * `error` therefore sees a raw 401 BEFORE `auth` has had any chance to
 * refresh and retry - it cannot "observe the final outcome".
 *
 * The consequence is deliberate: 401 is owned end-to-end by `authInterceptor`
 * (heal invisibly, or forceLogout), and `errorInterceptor` does NOT toast it.
 * Otherwise every routine token expiry would flash "session expired" a moment
 * before the data actually landed.
 *
 * The alternative - ordering [loading, error, auth] so `error` really is
 * outside the retry - is arguably cleaner and would remove that coupling. It
 * was not taken because this arrangement is covered by 12 passing specs and
 * concentrating 401 in one interceptor is easy to reason about. Revisit only
 * with those specs in hand.
 *
 * Everything else in this codebase is imported by its full path: one barrel
 * re-exporting forty components drags all forty into whichever chunk touches
 * it, silently defeating the route-level bundle split.
 */
export const HTTP_INTERCEPTORS_IN_ORDER: readonly HttpInterceptorFn[] = [
  loadingInterceptor,
  authInterceptor,
  errorInterceptor,
];
